/**
 * Displays a chronological progression of parameter readings.
 *
 * - 0 values: renders nothing
 * - 1–2 values: inline with arrow separators (val₁ → val₂)
 * - 3+ values: shows the latest value with a "(N values)" hover card listing all
 */
import {createStyles, Group, HoverCard, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

const ARROW_SEPARATOR = ' → ';
const MAX_ARROW_VALUES = 2;

const useStyles = createStyles(() => ({
  readingsTrigger: {
    cursor: 'pointer',
  },
}));

/**
 * Latest reading plus a hover card listing every reading. HoverCard manages the open/close
 * lifecycle, including the grace period that keeps the card open while the pointer travels from
 * the trigger onto the dropdown.
 *
 * When `disableHoverCard` is set, the "(N values)" cue still renders so the cell stays visibly
 * multi-value, but as plain non-link text with no HoverCard. A differences tooltip owns the cell's
 * hover in that case, so nesting a second overlay here would collide.
 */
function CollapsedReadings({
  latestValue,
  readingsList,
  count,
  disableHoverCard,
}: {
  latestValue: React.ReactNode;
  readingsList: Array<React.ReactNode>;
  count: number;
  disableHoverCard: boolean;
}): JSX.Element {
  const {classes} = useStyles();

  if (disableHoverCard) {
    return (
      <Group spacing="sm" align="center">
        <Text fz="xs" c="gray.9" component="div">
          {latestValue}
        </Text>
        <Text span={true} fz="xs" c="gray.9">
          ({count} values)
        </Text>
      </Group>
    );
  }

  return (
    <Group spacing="sm" align="center">
      <Text fz="xs" c="gray.9" component="div">
        {latestValue}
      </Text>
      <HoverCard
        width={200}
        shadow="md"
        withArrow={true}
        position="top"
        withinPortal={true}
        openDelay={300}
        closeDelay={100}
      >
        <HoverCard.Target>
          <UnstyledButton type="button" className={classes.readingsTrigger}>
            <Text span={true} fz="xs" c="blue.6">
              ({count} values)
            </Text>
          </UnstyledButton>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Stack spacing={2}>
            {readingsList.map((val, i) => (
              <Text key={i} fz="xs">
                {i + 1}. {val}
              </Text>
            ))}
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    </Group>
  );
}

export default function MultiValueDisplay({
  values,
  tooltipValues,
  disableReadingsHoverCard = false,
}: {
  values: Array<React.ReactNode>;
  /** Optional alternate representation for the hover card's readings list (defaults to `values`). */
  tooltipValues?: Array<React.ReactNode>;
  /**
   * When true, the "(N values)" cue renders as plain non-link text with no HoverCard, because a
   * cell-level differences tooltip owns this cell's hover. Defaults to the interactive HoverCard.
   */
  disableReadingsHoverCard?: boolean;
}): JSX.Element | null {
  if (values.length === 0) {
    return null;
  }

  if (values.length <= MAX_ARROW_VALUES) {
    return (
      <Text fz="xs" c="gray.9" component="div">
        {values.map((val, i) => (
          <React.Fragment key={i}>
            {i > 0 && ARROW_SEPARATOR}
            {val}
          </React.Fragment>
        ))}
      </Text>
    );
  }

  return (
    <CollapsedReadings
      latestValue={values[values.length - 1]}
      readingsList={tooltipValues ?? values}
      count={values.length}
      disableHoverCard={disableReadingsHoverCard}
    />
  );
}
